import { empty, Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { IEntity } from '../interfaces/entity';
import { messageService } from './messages/message.module';
import { Type } from '@angular/core';
import { KeyValuePair } from '../models/keyvaluepair';
import { RootInjectorGuard } from '../guards/RootInjectorGuard';
import { monitoringService } from './monitoring/monitoring.module';

export interface IDictionary<T>{
  [Key: string]: T[];
}

export interface IDataService{
  isUnique(val: string, id: number|string, propertyName: string): Observable<boolean>;
  isUniqueCombination(id: number|string, properties: KeyValuePair[]): Observable<boolean>;
}

export abstract class DataService<T extends IEntity> extends RootInjectorGuard implements IDataService {

  // static dictionary of
  private static cacheDictionary: IDictionary<any> = {};

  responseCache$: {[index: string]: any[]; } = {};

  // tslint:disable-next-line:no-inferrable-types
  cacheEnabled: boolean = false;
  private cacheName: string;

  // monitoringService: MonitoringService;

  constructor(protected type: Type<any>, protected name: string,
              protected router: Router) {
    super(type);
    this.cacheName = name;
  }

  get cache(){
    return (this.cacheEnabled === true) ? DataService.cacheDictionary[this.cacheName] || [] : [];
  }

  set cache(val: T[] ){
    if (this.cacheEnabled === true){
      DataService.cacheDictionary[this.cacheName]  = val;
    }
  }

  get CacheName(){
    return (this.cacheEnabled === true) ? this.cacheName : this.name;
  }

  set CacheName(name: string){
    if (name && (this.cacheEnabled === true)){
      this.cacheName = name;
      if (!this.cache){
        this.cache  = [];
      }
    }
  }

  setCache(key: string, val: T[]){
    if (this.cacheEnabled === true){
      this.cache = val;
    }
    else{
      // if cache not enabled then use responseCache
      this.responseCache$[key] = val;
    }
  }

  clearCache() {
    this.cache = [];
    this.responseCache$ = {};
  }

  resetAllCache() {
    for (const x of Object.keys(DataService.cacheDictionary)) {
      DataService.cacheDictionary[x] = [];
    }
  }

  getCache(): T[]{
    if (this.cacheEnabled === true){
      // this.info('Cache enabled');
      const cache = this.cache || [];
      // this.info('Cached Data:', cache);
      return cache;
    }
    else{
      // this.log('Cache disabled');
      return [];
    }
  }

  addToCache(data: T[] ){
    if (this.cacheEnabled === true){
      this.cache = this.cache.concat(data);
    }
  }

  replaceCache(data: T[] ){
    if (this.cacheEnabled === true){
      this.cache = data;
    }
  }

  removeItemFromCache(uid: string){
    if (this.cacheEnabled === true){
      this.removeItemFromArray(this.cache, uid);
    }
    else{
      Object.keys(this.responseCache$).forEach((e) => {
        if (Array.isArray(this.responseCache$[e])){
          this.removeItemFromArray(this.responseCache$[e], uid);
        }
      });
    }
  }

  private removeItemFromArray(arr: T[], uid: string){
    if (arr) {
      const index = arr.findIndex((x) => (x.uid === uid) || (x.id && (x.id.toString() === uid)));
      if (index > -1){
        arr.splice(index, 1);
      }
    }
  }

  updateOrAddToCache(entity: T){
    if (this.cacheEnabled === true){
      this.updateOrAddToArray(this.cache, entity);
    }
    else{
      Object.keys(this.responseCache$).forEach((e) => {
        if (Array.isArray(this.responseCache$[e])){
          this.updateOrAddToArray(this.responseCache$[e], entity);
        }
      });
    }
  }

  private updateOrAddToArray(arr: T[], entity: T){
    if (arr && Array.isArray(arr)) {
      const index = arr.findIndex((x) => x.id === entity.id);
      if (index > -1) {
        arr.splice(index, 1, entity);
      }
      else {
        arr.push(entity);
      }
    }
  }

  getByIdFromCache(id: number){
    return (id && this.cache) ? this.cache.find((i) => i.id === id) : undefined;
  }

  isUnique(val: string, id: number|string, propertyName: string): Observable<boolean>{
    if (val){
      const item = this.cache.find((i) => (i[propertyName]) && (i.id !== id)
        && (i[propertyName].toString().toLowerCase().replace(/\s/g, '') === val.toString().toLowerCase().replace(/\s/g, '')));
      return of(item ? false : true);
    }
    else{
      return of(true);
    }
  }

  isUniqueCombination(id: number|string, properties: KeyValuePair[]): Observable<boolean>{
    if (properties.length > 0){
      if ((!this.cache) || (this.cache.length === 0)){ return of(true); }
      let matchingRecords = this.cache;
      properties.forEach((r) => {
        const val = r.value.toString().toLowerCase().replace(/\s/g, '');
        matchingRecords = matchingRecords.filter((i) => (i[r.key]) && (i.id !== id)
        && (i[r.key].toString().toLowerCase().replace(/\s/g, '') === val));
      });
      return of((matchingRecords.length > 0) ? false : true);
    }
    else{
      return of(true);
    }
  }

  /**
   * Handle Error...
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  // tslint:disable-next-line:no-shadowed-variable
  protected handleError<T>(operation = 'operation', notifyUser?: boolean, result?: any) {
    return (error: any): Observable<T> => {
      // error.status may contain http code which can then be used to determine what to do next...
      // notify user
      if (notifyUser && error.status){
        let userErrorMessage = '';
        switch (error.status){
          case 500:
            userErrorMessage = 'The server was unable to complete the request due to an error';
            break;
          case 401:
            userErrorMessage = 'You have not been authorised to complete the request. Please login';
            this.router.navigate(['/logout']);
            break;
          case 404:
            userErrorMessage = 'Unable to process the request at this time. Please try again';
            break;
          // 460 is a custom message returned from webapi
          case 460:
            userErrorMessage = error.error;
            break;
          case 470:
            userErrorMessage = error.error;
            break;
          default:
            userErrorMessage = `The error status was ${(error.status) ? error.status : 'undefined'}`;
            break;
        }
        if (error.status === 470){
          messageService.info(`${operation} cancelled.  ${userErrorMessage}.`);
        }
        else{
          messageService.error(`${operation} failed.  ${userErrorMessage}.`);
        }
      }

      // log the error
      if (error.status === 470){
        this.info(`${operation} cancelled: ${error.message}`);
      }
      else{
        this.error(`${operation} failed: ${error.message}`);
      }

      // push the error to monitoring service
      monitoringService.logEvent(`${operation} failed`, {message: `${error.message}`});

      // Let the app keep running by returning an empty result.
      return result || of(null);
    };
  }

  /** Log a Service message with the MessageService */
  protected log(message: any, ...optionalParams: any[]) {
    monitoringService.logEvent(`Service for ${this.cacheName}: ${message}`, optionalParams);
  }

  protected error(message: any, ...optionalParams: any[]) {
    messageService.add(`Service for ${this.cacheName}: ${message}`, 'Error', optionalParams);
  }

  protected info(message: any, ...optionalParams: any[]) {
    messageService.add(`Service for ${this.cacheName}: ${message}`, 'Info', optionalParams);
  }

  protected debug(message: any, ...optionalParams: any[]) {
    messageService.add(`Service for ${this.cacheName}: ${message}`, 'Debug', optionalParams);
  }
}
